/**
 * PageLayout - Standardized layout for all application pages
 * Provides consistent spacing, structure, and UI patterns across the application
 */

import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Skeleton,
} from '@mui/material';

/**
 * Standard spacing constants for all pages
 * Consistent with AdminPageLayout spacing
 */
export const PAGE_SPACING = {
  pageTop: 3,           // Top padding of page container
  pageBottom: 3,        // Bottom padding of page container
  pageHorizontal: 3,    // Horizontal padding of page container
  sectionGap: 3,        // Gap between major sections
  cardPadding: 3,       // Padding inside cards/papers
  titleMargin: 2,       // Margin below page title
  formFieldGap: 2,      // Gap between form fields
  buttonGap: 2,         // Gap between action buttons
  tabContentPadding: 3, // Padding inside tab panels
} as const;

/**
 * Page layout props
 */
export interface PageLayoutProps {
  /** Page title */
  title: string;

  /** Action buttons in the header (e.g., Add, Save, Cancel) */
  headerActions?: React.ReactNode;

  /** Main page content */
  children: React.ReactNode;

  /** Whether the page is loading */
  isLoading?: boolean;

  /** Maximum width of the container */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

  /** Whether to wrap content in a Paper component */
  usePaper?: boolean;

  /** Additional content above the main content */
  headerContent?: React.ReactNode;
}

/**
 * Standardized page layout component
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  headerActions,
  children,
  isLoading = false,
  maxWidth = 'lg',
  usePaper = false,
  headerContent,
}) => {
  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: PAGE_SPACING.sectionGap,
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isLoading ? (
            <Skeleton variant="text" width="60%" height={40} />
          ) : (
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        {/* Header Actions */}
        {headerActions && !isLoading && (
          <Box
            sx={{
              display: 'flex',
              gap: PAGE_SPACING.buttonGap,
              alignItems: 'center',
            }}
          >
            {headerActions}
          </Box>
        )}
      </Box>

      {/* Additional Header Content */}
      {headerContent && !isLoading && (
        <Box>{headerContent}</Box>
      )}

      {/* Main Content */}
      <Box>
        {isLoading ? (
          <Skeleton variant="rectangular" height={400} />
        ) : (
          children
        )}
      </Box>
    </Box>
  );

  return (
    <Container
      maxWidth={maxWidth}
      sx={{
        py: PAGE_SPACING.pageTop,
        px: PAGE_SPACING.pageHorizontal,
      }}
    >
      {usePaper ? (
        <Paper
          elevation={1}
          sx={{
            p: PAGE_SPACING.cardPadding,
            borderRadius: 2,
          }}
        >
          {content}
        </Paper>
      ) : (
        content
      )}
    </Container>
  );
};

/**
 * Page section component - for grouping related content within a page
 */
export interface PageSectionProps {
  /** Section title */
  title?: string;

  /** Section description */
  description?: string;

  /** Section content */
  children: React.ReactNode;

  /** Whether to add elevation (card style) */
  elevated?: boolean;

  /** Action buttons for the section */
  actions?: React.ReactNode;
}

export const PageSection: React.FC<PageSectionProps> = ({
  title,
  description,
  children,
  elevated = false,
  actions,
}) => {
  const content = (
    <>
      {(title || actions) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: PAGE_SPACING.titleMargin,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Typography
                variant="h6"
                component="h2"
                gutterBottom={Boolean(description)}
                sx={{ fontWeight: 600 }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          {actions && (
            <Box
              sx={{
                display: 'flex',
                gap: PAGE_SPACING.buttonGap,
                alignItems: 'center',
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      )}

      <Box>{children}</Box>
    </>
  );

  if (elevated) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: PAGE_SPACING.cardPadding,
          borderRadius: 2,
        }}
      >
        {content}
      </Paper>
    );
  }

  return <Box>{content}</Box>;
};

/**
 * Page form field wrapper - standardizes form field spacing
 */
export interface PageFormFieldProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

export const PageFormField: React.FC<PageFormFieldProps> = ({
  children,
  fullWidth = true,
}) => {
  return (
    <Box
      sx={{
        mb: PAGE_SPACING.formFieldGap,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Page button group - standardizes button spacing
 */
export interface PageButtonGroupProps {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}

export const PageButtonGroup: React.FC<PageButtonGroupProps> = ({
  children,
  align = 'right',
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: PAGE_SPACING.buttonGap,
        justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
        flexWrap: 'wrap',
        mt: PAGE_SPACING.sectionGap,
      }}
    >
      {children}
    </Box>
  );
};
